import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const DAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const PALETTE = ['#2563eb', '#16a34a', '#9333ea', '#ea580c', '#0891b2', '#db2777', '#65a30d', '#7c3aed'];
const mins = (t) => { const [h, m] = (t || '').split(':').map(Number); return Number.isNaN(h) ? null : h * 60 + (m || 0); };

// Kural, gün d (0=Pzt) ve saat h dilimini kapsıyor mu (gece yarısını aşan aralık dahil)
const covers = (r, d, h) => {
  const s = mins(r.start_time), e = mins(r.end_time);
  const inDay = (day) => !r.weekdays.length || r.weekdays.includes(day);
  if (s == null || e == null) return inDay(d);
  const a = h * 60, b = a + 60;
  if (e > s) return inDay(d) && a < e && s < b;
  return (inDay(d) && b > s) || (inDay((d + 6) % 7) && a < e);
};

const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
// Kuralın d gününe düşen saat parçaları (gece yarısını aşan vardiya iki güne bölünür)
const segments = (r, d) => {
  const s = mins(r.start_time), e = mins(r.end_time);
  const inDay = (day) => !r.weekdays.length || r.weekdays.includes(day);
  if (s == null || e == null) return inDay(d) ? ['Tüm gün'] : [];
  if (e > s) return inDay(d) ? [`${hhmm(s)}–${hhmm(e)}`] : [];
  return [...(inDay((d + 6) % 7) && e > 0 ? [`00:00–${hhmm(e)}`] : []), ...(inDay(d) ? [`${hhmm(s)}–24:00`] : [])];
};
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

const DayDialog = ({ day, cell, experts, color, onClose }) => {
  const dt = new Date(`${day}T00:00:00`);
  const d = (dt.getDay() + 6) % 7;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="schedule-day-dialog">
        <DialogHeader><DialogTitle>{dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' })}</DialogTitle></DialogHeader>
        <div className="divide-y rounded border text-sm">
          {Array.from({ length: 24 }, (_, h) => {
            const ids = cell(d, h);
            return (
              <div key={h} className={`flex items-center gap-3 px-3 py-1 ${ids.length ? '' : 'bg-red-50'}`} data-testid={`schedule-day-hour-${h}`}>
                <span className="w-24 font-mono text-xs text-slate-500">{hhmm(h * 60)}–{hhmm(h * 60 + 60)}</span>
                {ids.length ? <span className="flex flex-wrap gap-1">{ids.map(i => <span key={i} className="rounded px-1.5 py-0.5 text-xs font-medium text-white" style={{ background: color[i] }}>{experts.find(e => e.id === i)?.name}</span>)}</span>
                  : <span className="text-xs font-bold text-red-600">Boşta</span>}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MonthView = ({ active, experts, color, cell }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [day, setDay] = useState(null);
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const lead = (first.getDay() + 6) % 7;
  const count = new Date(y, m, 0).getDate();
  const today = iso(new Date());
  const name = Object.fromEntries(experts.map(e => [e.id, e.name]));
  return (
    <div data-testid="schedule-month-view">
      <div className="mb-2 flex items-center gap-2">
        <Input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="schedule-month-input" />
        <span className="text-sm font-semibold text-slate-700">{MONTHS[m - 1]} {y}</span>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs">
        {DAYS.map(d => <div key={d} className="py-1 text-center font-semibold text-slate-600">{d}</div>)}
        {Array.from({ length: lead }, (_, i) => <div key={`l${i}`} />)}
        {Array.from({ length: count }, (_, i) => {
          const date = iso(new Date(y, m - 1, i + 1));
          const d = (lead + i) % 7;
          const empty = Array.from({ length: 24 }, (_, h) => h).filter(h => !cell(d, h).length).length;
          const rows = active.flatMap(r => segments(r, d).map(t => ({ uid: r.user_id, t })));
          return (
            <button key={date} type="button" onClick={() => setDay(date)} data-testid={`schedule-month-day-${date}`}
              className={`min-h-[88px] rounded border-2 p-1 text-left transition-colors hover:bg-slate-50 ${empty ? 'border-red-300' : 'border-slate-200'} ${date === today ? 'ring-2 ring-amber-400' : ''}`}>
              <div className="flex items-center justify-between">
                <span className={`font-bold ${date === today ? 'text-amber-600' : 'text-slate-700'}`}>{i + 1}</span>
                {empty > 0 && <span className="rounded bg-red-100 px-1 text-[10px] font-semibold text-red-700" data-testid={`schedule-month-empty-${date}`}>{empty === 24 ? 'Tüm gün boşta' : `${empty} saat boşta`}</span>}
              </div>
              <div className="mt-1 space-y-0.5">
                {rows.map((x, k) => <p key={k} className="truncate rounded px-1 text-[10px] text-white" style={{ background: color[x.uid] }}>{name[x.uid]} {x.t}</p>)}
              </div>
            </button>
          );
        })}
      </div>
      {day && <DayDialog day={day} cell={cell} experts={experts} color={color} onClose={() => setDay(null)} />}
    </div>
  );
};

export const ScheduleCalendar = ({ rules, experts, locations }) => {
  const [loc, setLoc] = useState('all');
  const [view, setView] = useState('week');
  const color = Object.fromEntries(experts.map((e, i) => [e.id, PALETTE[i % PALETTE.length]]));
  const active = rules.filter(r => color[r.user_id] && (loc === 'all' || !r.location_ids.length || r.location_ids.includes(loc)));
  const cell = (d, h) => [...new Set(active.filter(r => covers(r, d, h)).map(r => r.user_id))];
  let empty = 0;
  for (let d = 0; d < 7; d++) for (let h = 0; h < 24; h++) if (!cell(d, h).length) empty++;
  return (
    <div className="rounded-lg border p-3" data-testid="schedule-calendar">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-slate-800">Program Takvimi</p>
        <div className="flex rounded-md border p-0.5">
          {[['week', 'Haftalık'], ['month', 'Aylık']].map(([k, l]) => <button key={k} type="button" onClick={() => setView(k)} data-testid={`schedule-view-${k}`}
            className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${view === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>)}
        </div>
        <Select value={loc} onValueChange={setLoc}>
          <SelectTrigger className="w-56" data-testid="schedule-calendar-location"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm lokasyonlar</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        {view === 'week' && <span className={`rounded px-2 py-0.5 text-xs font-semibold ${empty ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid="schedule-calendar-empty">{empty ? `${empty} saatlik dilim boşta` : 'Tüm hafta kapsanıyor'}</span>}
        <span className="flex flex-wrap gap-2 text-xs">{experts.map(e => <span key={e.id} className="flex items-center gap-1"><span className="h-3 w-3 rounded-sm" style={{ background: color[e.id] }} />{e.name}</span>)}</span>
      </div>
      <p className="mb-2 text-[11px] text-slate-500">Tehlike sınıfı, iş tipi ve iş çeşidi kısıtlı kurallar da takvimde gösterilir; o dilimde sadece kuraldaki talepleri kapsar.</p>
      {view === 'month' ? <MonthView active={active} experts={experts} color={color} cell={cell} /> : <div className="overflow-x-auto">
        <div className="grid min-w-[720px] gap-px bg-slate-200 text-[10px]" style={{ gridTemplateColumns: '48px repeat(7, 1fr)' }}>
          <div className="bg-white" />
          {DAYS.map(d => <div key={d} className="bg-slate-50 py-1 text-center font-semibold text-slate-700">{d}</div>)}
          {Array.from({ length: 24 }, (_, h) => [
            <div key={`h${h}`} className="bg-slate-50 px-1 py-0.5 text-right text-slate-500">{String(h).padStart(2, '0')}:00</div>,
            ...DAYS.map((_, d) => {
              const ids = cell(d, h);
              return (
                <div key={`${d}-${h}`} title={ids.length ? ids.map(i => experts.find(e => e.id === i)?.name).join(', ') : 'Boşta'} data-testid={`schedule-cell-${d}-${h}`}
                  className="flex h-5 gap-px bg-white p-px" style={ids.length ? undefined : { backgroundImage: 'repeating-linear-gradient(45deg,#fee2e2 0 4px,#fecaca 4px 8px)' }}>
                  {ids.map(i => <span key={i} className="flex-1 rounded-sm" style={{ background: color[i] }} />)}
                </div>
              );
            }),
          ])}
        </div>
      </div>}
    </div>
  );
};
